import { z } from 'zod';
/**
 * Kalender-/Event-Contracts (shared, 07 – Datenmodell / 03 – FA).
 * Einfache RRULE-Untermenge (ADR-006): täglich/wöchentlich/monatlich/jährlich,
 * Intervall, optional Wochentage (weekly) und Enddatum (UNTIL).
 */
// ── Wiederholung ──────────────────────────────────────────────────
export const RECURRENCE_FREQS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const isoDate = z.string().refine((s) => !Number.isNaN(Date.parse(s)), 'Ungültiges Datum');
export const recurrenceSchema = z.object({
    freq: z.enum(RECURRENCE_FREQS),
    interval: z.number().int().positive().max(999).default(1),
    byWeekday: z.array(z.enum(WEEKDAYS)).max(7).optional(),
    until: isoDate.optional(),
    // Anzahl-Limit (RRULE COUNT). Vor allem für importierte Abo-Serien relevant,
    // die sonst endlos weiterliefen (ADR-087).
    count: z.number().int().positive().max(999).optional(),
});
// ── Calendar ──────────────────────────────────────────────────────
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Farbe als #rrggbb');
export const calendarDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    ownerMemberId: z.string().uuid().nullable(),
    isDefault: z.boolean(),
});
export const createCalendarSchema = z.object({
    name: z.string().min(1).max(60),
    color: hexColor,
    ownerMemberId: z.string().uuid().nullable().optional(),
    isDefault: z.boolean().optional(),
});
export const updateCalendarSchema = createCalendarSchema.partial();
// ── Teilnehmer ────────────────────────────────────────────────────
// Ein Familienkalender; Zugehörigkeit läuft über die Teilnahme am Termin.
// participant = nimmt teil (bekommt Erinnerung), info = soll es nur wissen.
export const ATTENDEE_KINDS = ['participant', 'info'];
export const attendeeSchema = z.object({
    memberId: z.string().uuid(),
    kind: z.enum(ATTENDEE_KINDS),
});
// ── Event ─────────────────────────────────────────────────────────
const eventBase = z.object({
    // calendarId optional: der Server ordnet Termine immer dem einen
    // Familienkalender zu (ADR-026). Für Rückwärtskompatibilität akzeptiert.
    calendarId: z.string().uuid().optional(),
    title: z.string().min(1).max(200),
    startsAt: isoDate,
    endsAt: isoDate,
    allDay: z.boolean().optional(),
    location: z.string().max(200).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    recurrence: recurrenceSchema.nullable().optional(),
    reminderMinutes: z.number().int().min(0).max(40320).nullable().optional(),
    attendees: z.array(attendeeSchema).max(50).optional(),
});
const endAfterStart = (d) => Date.parse(d.endsAt) >= Date.parse(d.startsAt);
export const createEventSchema = eventBase.refine(endAfterStart, {
    message: 'Ende darf nicht vor dem Beginn liegen',
    path: ['endsAt'],
});
/**
 * Geltungsbereich einer Änderung an einem Serientermin (ADR-087):
 * - `all`       = ganze Serie (Standard, rückwärtskompatibel zu alten Clients)
 * - `this`      = nur dieses Vorkommen (→ Ausnahme/Override)
 * - `following` = dieses und alle folgenden (→ Serie splitten)
 */
export const SERIES_SCOPES = ['all', 'this', 'following'];
export const updateEventSchema = eventBase.partial().extend({
    scope: z.enum(SERIES_SCOPES).optional(),
    // Start des betroffenen Vorkommens (Original, wie aus der Serie entfaltet).
    occurrenceStart: isoDate.optional(),
});
export const eventDtoSchema = z.object({
    id: z.string().uuid(),
    calendarId: z.string().uuid(),
    title: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    allDay: z.boolean(),
    location: z.string().nullable(),
    description: z.string().nullable(),
    rrule: z.string().nullable(),
    reminderMinutes: z.number().int().nullable(),
    createdBy: z.string().uuid().nullable(),
    attendees: z.array(attendeeSchema),
    // Aus einem externen iCal-Abo importiert (schreibgeschützt); color = Abo-Farbe.
    subscriptionId: z.string().uuid().nullable(),
    color: z.string().nullable(),
    // Abo mit „als Hintergrund anzeigen" (Feiertage/Ferien, ADR-089): Monatsansicht
    // tönt den Tag statt einen Balken zu zeichnen. Optional = ältere Server.
    renderAsBackground: z.boolean().optional(),
});
/** Eine einzelne (ggf. aus einer Wiederholung entfaltete) Vorkommnis-Instanz. */
export const occurrenceDtoSchema = eventDtoSchema.extend({
    occurrenceStart: z.string(),
    occurrenceEnd: z.string(),
    // Ursprünglicher Start dieses Vorkommens in der Serie (Schlüssel für Ausnahmen,
    // ADR-087). Weicht von occurrenceStart ab, wenn das Vorkommen verschoben wurde.
    originalStart: z.string().optional(),
    // true = dieses Vorkommen ist eine Ausnahme (einzeln geändert).
    isException: z.boolean().optional(),
});
// ── Ausnahmen von Serienterminen (ADR-087) ─────────────────────────
export const OVERRIDE_KINDS = ['cancelled', 'moved'];
// ── Kalender-Feed (ICS-Export, ADR-086) ────────────────────────────
export const calendarFeedStatusSchema = z.object({
    // Darf diese Person einen Feed haben? Erwachsene immer, Kinder nur nach
    // Freischaltung durch ein Elternteil, Gast nie.
    allowed: z.boolean(),
    active: z
        .object({
        createdAt: z.string(),
        lastUsedAt: z.string().nullable(),
    })
        .nullable(),
});
/** Antwort beim Erzeugen — die URL ist danach NIE wieder abrufbar (nur Hash gespeichert). */
export const calendarFeedCreatedSchema = z.object({
    httpsUrl: z.string(),
    webcalUrl: z.string(),
    createdAt: z.string(),
});
//# sourceMappingURL=calendar.js.map